import api, { getMediaAccessToken } from '../services/api';

export function getApiOrigin(): string {
  const base = (api.defaults.baseURL || '').replace(/\/$/, '');
  const origin = base.replace(/\/api$/i, '');
  if (typeof window !== 'undefined' && origin && origin === window.location.origin) {
    return '';
  }
  return origin;
}

export function absolutizeContentMediaUrls(html: string): string {
  if (!html) return html;
  const origin = getApiOrigin();
  if (!origin) return html;
  return html
    .replace(/(src=["'])(\/api\/content\/media-staging[^"']*)(["'])/gi, (_, a, path, c) => `${a}${origin}${path}${c}`)
    .replace(/(src=["'])(\/api\/content\/media[^"']*)(["'])/gi, (_, a, path, c) => `${a}${origin}${path}${c}`);
}

/** Remove session/media tokens from persisted or pasted HTML before re-adding the media-only token. */
export function stripMediaAccessTokens(html: string): string {
  if (!html) return html;
  let out = html.replace(/([?&])access_token=[^&#"'\s]*/gi, (_m, lead: string) => (lead === '?' ? '?' : ''));
  return out.replace(/\?&/g, '?').replace(/\?$/g, '').replace(/&&/g, '&');
}

export function appendMediaAccessTokenToHtml(html: string): string {
  const token = getMediaAccessToken();
  if (!token || !html) return html;
  const enc = encodeURIComponent(token);
  return html.replace(
    /(src=["'])([^"']*\/api\/content\/(?:media-staging|media)[^"']*)(["'])/gi,
    (_full, q1, url, q2) => {
      const cleaned = stripMediaAccessTokens(url as string);
      const sep = cleaned.includes('?') ? '&' : '?';
      return `${q1}${cleaned}${sep}access_token=${enc}${q2}`;
    },
  );
}

const FORBIDDEN_RICH_TAGS = new Set([
  'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'LINK', 'META', 'BASE', 'STYLE',
  'TEXTAREA', 'SELECT', 'BUTTON', 'SVG', 'MATH',
]);

function isAllowedRichUrl(url: string): boolean {
  const u = (url || '').trim();
  if (!u) return false;
  const lower = u.toLowerCase();
  if (u.startsWith('/') || u.startsWith('#') || lower.startsWith('blob:') || lower.startsWith('data:image/')) {
    return true;
  }
  return /^https?:\/\//i.test(u);
}

/** Drop script tags, event handlers, and javascript: URLs from stored rich HTML. */
export function sanitizeRichHtml(html: string): string {
  if (!html || html.trim().startsWith('{')) return html;
  if (typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // DOMParser documents are about:blank. Without a real base, browsers may
    // rewrite relative /api/content/media/... src to about:blank on serialize.
    if (typeof window !== 'undefined' && window.location.origin && window.location.origin !== 'null') {
      const base = doc.createElement('base');
      base.setAttribute('href', `${window.location.origin}/`);
      doc.head.insertBefore(base, doc.head.firstChild);
    }
    doc.querySelectorAll('*').forEach((el) => {
      if (FORBIDDEN_RICH_TAGS.has(el.tagName)) {
        el.remove();
        return;
      }
      for (let i = el.attributes.length - 1; i >= 0; i--) {
        const attr = el.attributes.item(i);
        if (!attr) continue;
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || name === 'srcdoc' || name === 'formaction' || name === 'xlink:href') {
          el.removeAttribute(attr.name);
          continue;
        }
        const raw = el.getAttribute(attr.name) ?? attr.value;
        if ((name === 'href' || name === 'src' || name === 'poster') && !isAllowedRichUrl(raw)) {
          el.removeAttribute(attr.name);
        }
      }
      if (el.tagName === 'A') {
        el.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return doc.body?.innerHTML ?? '';
  } catch {
    return '';
  }
}

/** Line breaks from plain text, paste, and iOS (Notes/Mail often use U+2028/U+2029). */
const LINE_BREAK_PATTERN = /\r\n|\r|\n|\u2028|\u2029|\u0085/g;

/** Collapse line breaks to spaces for single-line previews (content pane table, etc.). */
export function formatContentTextSingleLinePreview(text: string): string {
  return text.replace(LINE_BREAK_PATTERN, ' ').replace(/<br\s*\/?>/gi, ' ');
}

/**
 * Plain text from rich HTML with a space between block-level elements.
 * Safari Quill often keeps paragraphs in HTML while getText() omits separators.
 */
export function htmlToPlainWithBlockSpaces(html: string): string {
  const raw = html.trim();
  if (!raw) return '';
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const blockTags = new Set([
        'P',
        'DIV',
        'LI',
        'H1',
        'H2',
        'H3',
        'H4',
        'H5',
        'H6',
        'TR',
        'TD',
        'TH',
      ]);
      const inlineText = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          return (node.textContent ?? '').replace(/\s+/g, ' ');
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return '';
        const el = node as HTMLElement;
        if (el.tagName === 'BR') return ' ';
        return Array.from(el.childNodes).map(inlineText).join('');
      };
      const blockText = (el: HTMLElement): string =>
        Array.from(el.childNodes)
          .map(inlineText)
          .join('')
          .replace(/\s+/g, ' ')
          .trim();
      const blocks: string[] = [];
      const collectBlocks = (node: Node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as HTMLElement;
        if (blockTags.has(el.tagName)) {
          const t = blockText(el);
          if (t) blocks.push(t);
          return;
        }
        Array.from(el.childNodes).forEach(collectBlocks);
      };
      Array.from(doc.body.childNodes).forEach(collectBlocks);
      if (blocks.length > 0) {
        return blocks.join(' ');
      }
      const fallback = (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (fallback) return fallback;
    } catch {
      // fall through to regex
    }
  }
  return formatContentTextSingleLinePreview(
    raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
      .replace(/<\/div>\s*<div[^>]*>/gi, ' ')
      .replace(/<[^>]+>/g, ''),
  );
}

/** Best-effort single-line label for the content pane (plain text + rich HTML fallback). */
export function getContentPanePreviewText(text: string, richTextHtml?: string | null): string {
  const fromPlain = formatContentTextSingleLinePreview(text ?? '');
  const rich = richTextHtml?.trim();
  if (!rich) return fromPlain;
  const fromRich = formatContentTextSingleLinePreview(htmlToPlainWithBlockSpaces(rich));
  if (!fromPlain) return fromRich;
  if (!fromRich) return fromPlain;
  if (!/\s/.test(fromPlain) && /\s/.test(fromRich)) return fromRich;
  return fromRich.length > fromPlain.length ? fromRich : fromPlain;
}

/**
 * Plain text for search, duplicate detection, and embeddings — same rules as the editor save
 * ({@link ContentEditor} cleanText) and server stored text (trim; collapse whitespace; blank → single space).
 */
export function contentSearchablePlainText(text: string, richTextHtml?: string | null): string {
  const fromPreview = getContentPanePreviewText(text ?? '', richTextHtml);
  let normalized = fromPreview.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    normalized = ' ';
  }
  return normalized;
}

function looksLikeRichHtml(text: string): boolean {
  return /<(?:p|br|div|span|img|a|ul|ol|li|strong|em|h[1-6]|blockquote)\b/i.test(text);
}

/** Hebrew, Arabic, and related scripts used for content direction in the editor. */
const RTL_SCRIPT = /[\u0590-\u05FF\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/** Plain text extracted from editor HTML, Quill delta JSON, or raw string (for direction heuristics). */
export function contentPlainTextForDirection(raw: string): string {
  const r = (raw ?? '').trim();
  if (!r) return '';
  if (r.startsWith('{')) {
    try {
      const doc = JSON.parse(r) as { ops?: { insert?: unknown }[] };
      return (doc.ops ?? [])
        .map((op) => (typeof op.insert === 'string' ? op.insert : ' '))
        .join('');
    } catch {
      return r;
    }
  }
  if (looksLikeRichHtml(r)) {
    return htmlToPlainWithBlockSpaces(r);
  }
  return r;
}

/** True when content is predominantly RTL script (Hebrew/Arabic etc.) — used for read-only alignment. */
export function contentTextLooksRtl(raw: string): boolean {
  return RTL_SCRIPT.test(contentPlainTextForDirection(raw));
}

function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text with newlines → HTML for Quill / read-only preview (line breaks as &lt;br&gt;). */
function plainTextToDisplayHtml(text: string): string {
  const lines = text.split(LINE_BREAK_PATTERN);
  const body = lines.map((line) => escapeHtmlText(line)).join('<br>');
  return `<p>${body || '<br>'}</p>`;
}

/** HTML ready for Quill or read-only preview (sanitized, absolute media URLs + media-only token). */
export function prepareContentRichPreviewHtml(html: string | undefined): string {
  if (!html) return '';
  if (html.trim().startsWith('{')) return html;
  const trimmed = sanitizeRichHtml(html.trim());
  if (!looksLikeRichHtml(trimmed)) {
    return appendMediaAccessTokenToHtml(absolutizeContentMediaUrls(plainTextToDisplayHtml(html)));
  }
  return appendMediaAccessTokenToHtml(absolutizeContentMediaUrls(trimmed));
}

/** True if raw editor value (HTML, plain text, or Quill Delta JSON) is non-empty for preview purposes. */
export function rawStringHasPreviewableBody(raw: string | undefined): boolean {
  const r = (raw ?? '').trim();
  if (!r) return false;
  if (r.startsWith('{')) {
    try {
      const doc = JSON.parse(r) as { ops?: unknown[] };
      return Array.isArray(doc?.ops) && doc.ops.length > 0;
    } catch {
      return true;
    }
  }
  const stripped = r.replace(/<[^>]*>/g, '').trim();
  if (stripped.length > 0) return true;
  return /<(img|video|audio)\b/i.test(r);
}
